import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import HomeHeader from '../Components/Home/HomeHeader';
import SidebarMenu from '../Components/Analysis/SidebarMenu';
import { Layout, Tabs, Tag, Table, Modal, Slider, InputNumber, Button } from '@arco-design/web-react';
import HomePagination from '../Components/Home/HomePagination';
import { PlatformIcon } from '../Components/Analysis/AnalysisComponents';

import ProgressBar from '../Components/PrivateCustomer/ProgressBar';
import PaginationToolbar from '../Components/PrivateCustomer/PaginationToolbar';
import InputWithTags from '../Components/PrivateCustomer/InputWithTags';
import TableFilter from '../Components/Common/TableFilter';
import DateFilter from '../Components/Common/DateFilter';
import RegionFilter from '../Components/Common/RegionFilter';
import { getTaskListApi, getMarketListByTaskIdApi, getMarketProgressApi } from '../Api/api.js';
import { getAuthToken } from '../Api/config.js';
import { performanceMonitor } from '../Api/config';
const { Content } = Layout;
const marketStateMap = { finish: '已私信', initial: '未私信', processing: '私信中' };
export default function PrivateCustomer() {
  const location = useLocation();
  
  // 任务相关状态
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]); // 有私信客户的任务
  // 移除平台筛选功能，始终显示全部任务
  const [selectedTaskId, setSelectedTaskId] = useState(() => {
    // 页面刷新时从localStorage恢复任务ID
    return localStorage.getItem('privateCustomer_selectedTaskId') || null;
  });
  const [taskLoading, setTaskLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 标记是否为初始加载
  // 详情相关状态
  const [detailData, setDetailData] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // 其它状态
  const [inputValue, setInputValue] = useState('');
  const [activeTab, setActiveTab] = useState('customers');
  // 进度条数据状态
  const [progressData, setProgressData] = useState({ num: 0, sum: 0, state: 0 });
  // 设置弹窗相关状态
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [privateMessageInterval, setPrivateMessageInterval] = useState(71);
  
  // 抖音账号相关状态
  const [douyinAccounts, setDouyinAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [hasInitialAccount, setHasInitialAccount] = useState(false);
  const [messagingAccounts, setMessagingAccounts] = useState(new Set()); // 跟踪正在私信的账号
  // 标签数据状态 - 移除useState声明，改为从currentTask获取
  
  // 筛选相关状态
  const [filters, setFilters] = useState({
    commentTime: '',
    userNickname: '',
    userRegion: ''
  });
  
  // 筛选处理函数
  const handleFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 筛选后的详情数据
  const filteredDetailData = useMemo(() => {
    return detailData.filter(item => {
      const matchTime = !filters.commentTime || 
        (item.comment_time && item.comment_time.includes(filters.commentTime));
      const matchNickname = !filters.userNickname || 
        (item.user_name && item.user_name.toLowerCase().includes(filters.userNickname.toLowerCase()));
      const matchRegion = !filters.userRegion || 
        (item.user_region && item.user_region.includes(filters.userRegion));
      
      return matchTime && matchNickname && matchRegion;
    });
  }, [detailData, filters]);
  
  // 缓存机制
  const cacheRef = useRef(new Map());
  
  // 防抖机制
  const debounceTimerRef = useRef(null);
  
  // 容器宽度测量
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // 请求缓存机制
  const requestCacheRef = useRef(new Map());
  const generateCacheKey = (taskId, page, size) => `${taskId}_${page}_${size}`;

  // 处理从Analysis页面传递过来的参数
  useEffect(() => {
    if (location.state) {
      const { selectedTaskId: taskId } = location.state;
      if (taskId) {
        setSelectedTaskId(taskId);
      }
    }
  }, [location.state]);
  
  // 当任务数据加载完成且有选中任务时，设置默认消息内容
  useEffect(() => {
    if (selectedTaskId && filteredTasks.length > 0 && !inputValue) {
      const selectedTask = filteredTasks.find(task => task.task_id === selectedTaskId);
      if (selectedTask && selectedTask.keyword) {
        const defaultMessage = `有${selectedTask.keyword}需求吗？`;
        setInputValue(defaultMessage);
      }
    }
  }, [selectedTaskId, filteredTasks, inputValue]);

  // 获取抖音账号列表的ref，避免循环依赖
  const fetchDouyinAccountsRef = useRef();
  
  const fetchDouyinAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const response = await fetch('http://127.0.0.1:3010/get_douyin_accounts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 200 && result.data && result.data.accounts) {
          // 将API返回的账号数据转换为表格需要的格式
          const formattedAccounts = result.data.accounts.map((account, index) => {
            // 保留原始的task_id，在表格渲染时再转换为任务标题
            const taskTitle = account.task_id || '无';
            
            return {
              key: account.user_id || `account_${index}`,
              account: account.username || '未知用户',
              currentTask: taskTitle,
              interval: `${account.marketing_count || 0}/99`,
              action: '',
              state: account.state,
              user_id: account.user_id,
              task_id: account.task_id,
              update_timestamp: account.update_timestamp,
              marketing_count: account.marketing_count
            };
          });
          
          // 如果没有已登录账号且还没有添加过初始账号，添加一个默认的未登录账号
          if (formattedAccounts.length === 0 && !hasInitialAccount) {
            formattedAccounts.push({
              key: 'default_1',
              account: '未登录，第一次登录可能要等待1-5分钟',
              currentTask: '无',
              interval: '0/99',
              action: '',
              state: 'not_logged_in',
              user_id: null
            });
            setHasInitialAccount(true);
          }
          
          setDouyinAccounts(formattedAccounts);
          console.log('获取抖音账号成功:', formattedAccounts);
          console.log('账号状态详情:', formattedAccounts.map(acc => ({ 
            username: acc.account, 
            state: acc.state, 
            user_id: acc.user_id 
          })));
        } else {
          console.error('获取抖音账号失败:', result.msg);
          // 如果API失败且没有初始账号，添加默认账号
          if (!hasInitialAccount) {
            setDouyinAccounts([{
              key: 'default_1',
              account: '未登录，第一次登录可能要等待1-5分钟',
              currentTask: '无',
              interval: '0/99',
              action: '',
              state: 'not_logged_in',
              user_id: null
            }]);
            setHasInitialAccount(true);
          }
        }
      } else {
        console.error('请求失败:', response.status);
        // 如果请求失败且没有初始账号，添加默认账号
        if (!hasInitialAccount) {
          setDouyinAccounts([{
            key: 'default_1',
            account: '未登录，第一次登录可能要等待1-5分钟',
            currentTask: '无',
            interval: '0/99',
            action: '',
            state: 'not_logged_in',
            user_id: null
          }]);
          setHasInitialAccount(true);
        }
      }
    } catch (error) {
      console.error('获取抖音账号出错:', error);
      // 如果出错且没有初始账号，添加默认账号
      if (!hasInitialAccount) {
        setDouyinAccounts([{
          key: 'default_1',
          account: '未登录，第一次登录可能要等待1-5分钟',
          currentTask: '无',
          interval: '0/99',
          action: '',
          state: 'not_logged_in',
          user_id: null
        }]);
        setHasInitialAccount(true);
      }
    } finally {
      setAccountsLoading(false);
    }
  }, [hasInitialAccount, tasks]);
  
  // 将fetchDouyinAccounts存储到ref中
  fetchDouyinAccountsRef.current = fetchDouyinAccounts;

  // 处理刷新抖音账号数据
  const handleRefreshAccounts = useCallback(() => {
    fetchDouyinAccountsRef.current?.();
  }, []);

  // 获取所有任务
  useEffect(() => {
    async function fetchTasks() {
      setLoading(true);
      try {
        const res = await getTaskListApi(0, 1000);
        if (res && (res.status === 200 || res.msg === 'success') && res.data && Array.isArray(res.data.task_list)) {
          setTasks(res.data.task_list);
        } else {
          setTasks([]);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchTasks();
  }, []);



  // 获取单个任务的私信状态（优化版 - 仅获取进度数据）
  const getTaskMarketState = useCallback(async (task) => {
    const cacheKey = `${task.task_id}`;
    const startTime = Date.now();
    
    // 检查缓存
    if (cacheRef.current.has(cacheKey)) {
      performanceMonitor.recordApiCall('getTaskMarketState', startTime, Date.now(), true);
      return cacheRef.current.get(cacheKey);
    }
    
    try {
      // 只获取进度数据，不获取私信列表（减少API调用）
      const progressRes = await getMarketProgressApi(task.task_id);
      
      const endTime = Date.now();
      performanceMonitor.recordApiCall('getTaskMarketState', startTime, endTime, false);
      
      // 确定任务的私信状态
      let marketState = 'initial'; // 默认未私信
      let num = 0, sum = 0, state = 0; // 初始化进度变量
      let hasPrivateCustomers = false;
      
      if (progressRes && progressRes.status === 200 && progressRes.data) {
        ({ num, sum, state } = progressRes.data);
        if (state === 3 || (num > 0 && num === sum)) {
          marketState = 'finish'; // 已完成私信（state=3或进度100%）
          hasPrivateCustomers = sum > 0; // 如果有总数，说明有私信客户
        } else if (num > 0 && num < sum) {
          marketState = 'processing'; // 私信中
          hasPrivateCustomers = true;
        } else if (sum > 0) {
          marketState = 'initial'; // 未开始私信但有客户
          hasPrivateCustomers = true;
        }
      }
      
      // 计算进度百分比
      const progressPercentage = sum > 0 ? Math.round((num / sum) * 100) : 0;
      
      const result = {
        ...task,
        market_state: marketState,
        progress: progressPercentage,
        progressData: { num, sum, state },
        hasPrivateCustomers
      };
      
      // 缓存结果（5分钟过期）
      cacheRef.current.set(cacheKey, result);
      setTimeout(() => {
        cacheRef.current.delete(cacheKey);
      }, 5 * 60 * 1000);
      
      return result;
    } catch (e) {
      
      performanceMonitor.recordApiCall('getTaskMarketState', startTime, Date.now(), false);
      const result = { 
        ...task, 
        market_state: 'initial', 
        progress: 0,
        progressData: { num: 0, sum: 0, state: 0 },
        hasPrivateCustomers: false 
      };
      return result;
    }
  }, []);



  // 批量获取任务状态的函数
  const batchFetchTaskStates = useCallback(async (taskList) => {
    const batchSize = 3; // 每批处理3个任务，避免服务器压力
    const results = [];
    
    for (let i = 0; i < taskList.length; i += batchSize) {
      const batch = taskList.slice(i, i + batchSize);
      const batchPromises = batch.map(async (task) => {
        try {
          return await getTaskMarketState(task);
        } catch (error) {
          console.error(`获取任务${task.task_id}状态失败:`, error);
          return {
            ...task,
            market_state: 'initial',
            progress: 0,
            progressData: { num: 0, sum: 0, state: 0 },
            hasPrivateCustomers: false
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // 每批之间添加小延迟，避免API请求过于频繁
      if (i + batchSize < taskList.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    return results;
  }, [getTaskMarketState]);



  // 页面加载时获取抖音账号数据
  useEffect(() => {
    fetchDouyinAccountsRef.current?.();
  }, []);

  // 处理新增抖音账号按钮点击
  const handleAddDouyinAccount = useCallback(() => {
    // 检查是否已经存在未登录的账号
    const hasNotLoggedInAccount = douyinAccounts.some(account => account.state === 'not_logged_in');
    
    // 如果已经存在未登录账号，则不允许再次添加
    if (hasNotLoggedInAccount) {
      return;
    }
    
    // 在现有账号列表中添加新的未登录账号
    const newAccount = {
      key: `new_${Date.now()}`,
      account: '未登录，第一次登录可能要等待1-5分钟',
      currentTask: '无',
      interval: '0/99',
      action: '',
      state: 'not_logged_in',
      user_id: null
    };
    setDouyinAccounts(prev => [...prev, newAccount]);
  }, [douyinAccounts]);



  // 处理登录抖音账号按钮点击
  const handleLoginDouyinAccount = useCallback(async (accountData) => {
    try {
      // 先调用后端登录接口
      const response = await fetch('http://127.0.0.1:3010/login_douyin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        referrerPolicy: 'strict-origin-when-cross-origin'
      });
      
      if (response.ok) {
        // 接口调用成功后，跳转到浏览器登录抖音账号
        const loginUrl = 'https://www.douyin.com/passport/web/login/';
        const loginWindow = window.open(loginUrl, '_blank');
        
        // 监听窗口焦点事件，当用户完成登录回到页面时自动刷新账号数据
        const handleFocus = () => {
          // 延迟一下再刷新，确保登录状态已经同步
          setTimeout(() => {
            fetchDouyinAccounts();
          }, 1000);
        };
        
        window.addEventListener('focus', handleFocus);
        
        // 添加定时轮询，每30秒检查一次登录状态
        const pollInterval = setInterval(() => {
          fetchDouyinAccounts();
        }, 30000);
        
        // 清理事件监听器和定时器（2分钟后自动清理）
        setTimeout(() => {
          window.removeEventListener('focus', handleFocus);
          clearInterval(pollInterval);
        }, 120000);
        
      } else {
        console.error('调用登录接口失败:', response.status);
      }
    } catch (error) {
      console.error('登录接口请求出错:', error);
      // 即使接口失败，也尝试跳转到登录页面
      const loginUrl = 'https://www.douyin.com/passport/web/login/';
      window.open(loginUrl, '_blank');
    }
  }, [selectedTaskId, inputValue]);

  // 处理开始私信按钮点击
  const handleStartMessaging = useCallback(async (accountData) => {
    try {
      // 检查必要参数
      console.log('开始私信 - 账号数据:', accountData);
      
      if (!accountData.task_id) {
        console.error('task_id 缺失，无法开始私信');
        console.error('当前账号数据:', accountData);
        console.error('当前选中的任务ID:', selectedTaskId);
        // 如果没有task_id，尝试使用当前选中的任务ID
        if (!selectedTaskId) {
          console.error('selectedTaskId 也为空，无法开始私信');
          alert('请先选择一个任务，然后再开始私信');
          return;
        }
        console.log('使用当前选中的任务ID作为备用:', selectedTaskId);
      }
      
      const finalTaskId = accountData.task_id || selectedTaskId;
      if (!finalTaskId) {
        console.error('无法获取有效的 task_id');
        alert('无法获取有效的任务ID，请刷新页面后重试');
        return;
      }
      
      console.log('最终使用的 task_id:', finalTaskId);
      
      // 验证消息内容
      if (!inputValue || inputValue.trim() === '') {
        console.error('消息内容不能为空');
        alert('请先输入私信内容');
        return;
      }
      
      const requestBody = {
        user_id: accountData.user_id,
        task_id: finalTaskId,
        message_text: inputValue.trim()
      };
      
      console.log('请求参数:', requestBody);
      
      // 调用开始私信接口
      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('http://127.0.0.1:3010/start_marketing', {
        method: 'POST',
        headers: headers,
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        if ((result.status === 200 && result.msg === 'success') || result.status === 'success' || result.msg === 'success') {
          // 将账号添加到私信中状态
          setMessagingAccounts(prev => new Set([...prev, accountData.user_id]));
          console.log('开始私信成功:', accountData.user_id, 'task_id:', result.task_id);
          
          // 直接更新账号的task_id状态，显示当前选中的任务标题
          setDouyinAccounts(prev => 
            prev.map(account => 
              account.user_id === accountData.user_id 
                ? { ...account, task_id: selectedTaskId, currentTask: selectedTaskId }
                : account
            )
          );
        } else {
          console.error('开始私信失败:', result);
        }
      } else {
        console.error('开始私信请求失败:', response.status);
      }
    } catch (error) {
      console.error('开始私信请求出错:', error);
    }
  }, [selectedTaskId, inputValue]);

  // 处理停止私信按钮点击
  const handleStopMessaging = useCallback(async (accountData) => {
    try {
      // 检查必要参数
      console.log('停止私信 - 账号数据:', accountData);
      
      if (!accountData.task_id) {
        console.error('task_id 缺失，无法停止私信');
        console.error('当前账号数据:', accountData);
        console.error('当前选中的任务ID:', selectedTaskId);
        // 如果没有task_id，尝试使用当前选中的任务ID
        if (!selectedTaskId) {
          console.error('selectedTaskId 也为空，无法停止私信');
          alert('请先选择一个任务，然后再停止私信');
          return;
        }
        console.log('使用当前选中的任务ID作为备用:', selectedTaskId);
      }
      
      const finalTaskId = accountData.task_id || selectedTaskId;
      if (!finalTaskId) {
        console.error('无法获取有效的 task_id');
        alert('无法获取有效的任务ID，请刷新页面后重试');
        return;
      }
      
      console.log('最终使用的 task_id:', finalTaskId);
      
      const requestBody = {
        user_id: accountData.user_id,
        task_id: finalTaskId
      };
      
      console.log('停止私信请求参数:', requestBody);
      
      // 调用停止私信接口
      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('http://127.0.0.1:3010/stop_marketing', {
        method: 'POST',
        headers: headers,
        referrerPolicy: 'strict-origin-when-cross-origin',
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        if ((result.status === 200 && result.msg === 'success') || result.status === 'success' || result.msg === 'success') {
          // 从私信中状态移除账号
          setMessagingAccounts(prev => {
            const newSet = new Set(prev);
            newSet.delete(accountData.user_id);
            return newSet;
          });
          console.log('停止私信成功:', accountData.user_id);
          
          // 直接更新账号的task_id状态，显示'无'
          setDouyinAccounts(prev => 
            prev.map(account => 
              account.user_id === accountData.user_id 
                ? { ...account, task_id: null, currentTask: '无' }
                : account
            )
          );
        } else {
          console.error('停止私信失败:', result.msg);
        }
      } else {
        console.error('停止私信请求失败:', response.status);
      }
    } catch (error) {
      console.error('停止私信请求出错:', error);
    }
  }, [selectedTaskId]);

  // 平台筛选+分析状态筛选（优化版：智能获取状态）
  useEffect(() => {
    if (tasks.length === 0) {
      setFilteredTasks([]);
      return;
    }
    
    setTaskLoading(true);
    
    async function processFilteredTasks() {
      try {
        // 只进行分析状态筛选，显示所有平台的已完成分析任务
        let platformTasks = tasks.filter(item => item.analysis_state === 'finish');
        
        // 如果有指定的任务ID（从Analysis页面传递过来），确保该任务包含在筛选结果中
        if (location.state?.selectedTaskId) {
          const targetTask = tasks.find(task => task.task_id === location.state.selectedTaskId);
          if (targetTask && !platformTasks.find(task => task.task_id === location.state.selectedTaskId)) {
            platformTasks = [targetTask, ...platformTasks];
          }
        }
        
        // 智能状态获取策略：
        // 1. 如果任务数量较少（≤5个），批量获取所有状态
        // 2. 如果任务数量较多，只获取前5个任务的状态，其余保持默认状态
        if (platformTasks.length <= 5) {
          // 任务较少，直接使用API返回的原始状态
          const tasksWithOriginalStates = platformTasks.map(task => ({
            ...task,
            // 直接使用API返回的原始market_state
            market_state: task.market_state || 'initial',
            progress: task.market_state === 'finish' ? 100 : (task.market_progress || 0),
            progressData: { num: 0, sum: 0, state: 0 },
            hasPrivateCustomers: task.market_state !== 'initial'
          }));
          setFilteredTasks(tasksWithOriginalStates);
        } else {
           // 任务较多，只获取前5个的状态
           const priorityTasks = platformTasks.slice(0, 5);
          
          // 保留任务的原始状态信息，包括API返回的market_state
          const initialTasks = platformTasks.map(task => ({
            ...task,
            // 直接使用API返回的原始market_state，不做任何修改
            market_state: task.market_state || 'initial',
            progress: task.market_state === 'finish' ? 100 : (task.market_progress || 0),
            progressData: { num: 0, sum: 0, state: 0 },
            hasPrivateCustomers: task.market_state !== 'initial'
          }));
          setFilteredTasks(initialTasks);
          
          // 然后异步获取前5个任务的真实状态
          const priorityTasksWithStates = await batchFetchTaskStates(priorityTasks);
          
          // 更新前5个任务的状态
          setFilteredTasks(prevTasks => {
            const updatedTasks = [...prevTasks];
            priorityTasksWithStates.forEach(updatedTask => {
              const index = updatedTasks.findIndex(t => t.task_id === updatedTask.task_id);
              if (index !== -1) {
                updatedTasks[index] = updatedTask;
              }
            });
            return updatedTasks;
          });
        }
      
        // 任务选中逻辑
        if (isInitialLoad) {
          let targetTaskId = null;
          if (location.state?.selectedTaskId) {
            // 从其他页面传递过来的任务ID优先级最高
            targetTaskId = location.state.selectedTaskId;
            setSelectedTaskId(targetTaskId);
          } else if (selectedTaskId && platformTasks.find(task => task.task_id === selectedTaskId)) {
            // 如果缓存的任务ID在当前筛选结果中，保持选中
            targetTaskId = selectedTaskId;
          } else if (platformTasks.length > 0) {
            // 否则默认选中第一个任务
            targetTaskId = platformTasks[0].task_id;
            setSelectedTaskId(targetTaskId);
          }
          
          setIsInitialLoad(false);
        }
        
      } catch (error) {
        console.error('处理筛选任务时出错:', error);
        setFilteredTasks([]);
      } finally {
        setTaskLoading(false);
      }
    }
    
    processFilteredTasks();
    
    // eslint-disable-next-line
  }, [tasks, location.state, batchFetchTaskStates]);

  // 切换任务时重置分页、输入框内容并缓存任务ID
  useEffect(() => {
    setCurrentPage(1);
    setInputValue(''); // 重置输入框内容
    // 缓存选中的任务ID到localStorage
    if (selectedTaskId) {
      localStorage.setItem('privateCustomer_selectedTaskId', selectedTaskId);
    } else {
      localStorage.removeItem('privateCustomer_selectedTaskId');
    }
  }, [selectedTaskId]);

  // 新增：专门处理页大小变化的函数
  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  // 获取详情数据（私信用户列表）
  useEffect(() => {
    if (!selectedTaskId || filteredTasks.length === 0) {
      return;
    }
    
    // 确保选中的任务在筛选结果中存在
    const taskExists = filteredTasks.find(task => task.task_id === selectedTaskId);
    if (!taskExists) {
      return;
    }
    
    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 设置防抖延迟
    debounceTimerRef.current = setTimeout(() => {
      async function fetchDetail() {
         const cacheKey = generateCacheKey(selectedTaskId, currentPage, pageSize);
         
         // 检查缓存
         if (requestCacheRef.current.has(cacheKey)) {
           const cachedData = requestCacheRef.current.get(cacheKey);
           setDetailData(cachedData.data);
           setDetailTotal(cachedData.total);
           return;
         }
         
         setLoading(true);
         try {
           const offset = (currentPage - 1) * pageSize;
           const res = await getMarketListByTaskIdApi(selectedTaskId, offset, pageSize);
          if (res && (res.status === 200 || res.msg === 'success') && res.user_link_list && Array.isArray(res.user_link_list)) {
            // 处理Unicode编码的中文字符
            const decodeUnicode = (str) => {
              if (!str) return str;
              try {
                return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) => {
                  return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
                });
              } catch (e) {
                return str;
              }
            };
            
            const processedData = res.user_link_list.map(item => {
              // 获取并解码私信结果
              const rawResult = item['\u79c1\u4fe1\u7ed3\u679c'] || item['私信结果'] || item.market_state;
              const decodedResult = decodeUnicode(rawResult);
              
              let marketState = 'initial';
              let displayResult = decodedResult;
              
              // 根据解码后的结果判断状态
              if (decodedResult && decodedResult.includes('已私信')) {
                marketState = 'finish';
                displayResult = '已私信';
              } else if (decodedResult && (decodedResult.includes('私信中') || decodedResult.includes('……'))) {
                marketState = 'processing';
                displayResult = '私信中';
              } else if (decodedResult && decodedResult.includes('未私信')) {
                marketState = 'initial';
                displayResult = '未私信';
              } else {
                // 如果无法识别，保持原始解码结果
                marketState = 'initial';
                displayResult = decodedResult || '未私信';
              }
              
              return {
                key: item.user_link || Math.random().toString(36).substr(2, 9),
                user_name: decodeUnicode(item['\u7528\u6237\u6635\u79f0'] || item['用户昵称'] || item.user_name),
                user_region: decodeUnicode(item['\u0049\u0050\u5730\u5740'] || item['IP地址'] || item.user_region),
                comment_time: item['评论时间'] || item.comment_time,
                comment_content: decodeUnicode(item['\u8bc4\u8bba\u5185\u5bb9'] || item['评论内容'] || item.comment_content),
                market_state: marketState,
                market_result_display: displayResult,
                user_link: item.user_link || '',
                content_link: item['\u5185\u5bb9\u94fe\u63a5'] || item['内容链接'] || ''
              };
            });
            
            setDetailData(processedData);
            setDetailTotal(res.total_count || 0);
            
            // 缓存结果（5分钟过期）
            const cacheData = {
              data: processedData,
              total: res.total_count || 0,
              timestamp: Date.now()
            };
            requestCacheRef.current.set(cacheKey, cacheData);
            setTimeout(() => {
              requestCacheRef.current.delete(cacheKey);
            }, 5 * 60 * 1000);
          } else {
            setDetailData([]);
            setDetailTotal(0);
          }
        } catch (error) {
    
          setDetailData([]);
          setDetailTotal(0);
        } finally {
          setLoading(false);
        }
      }
      
      // 从filteredTasks中获取进度数据（避免重复API调用）
      const currentTask = filteredTasks.find(task => task.task_id === selectedTaskId);
      if (currentTask && currentTask.progressData) {
        setProgressData({
          num: currentTask.progressData.num || 0,
          sum: currentTask.progressData.sum || 0,
          state: currentTask.progressData.state || 0
        });
      }
      
      fetchDetail();
      // 移除fetchProgress()调用，使用缓存的进度数据
    }, 300); // 300ms防抖延迟
    
    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedTaskId, currentPage, pageSize, filteredTasks]);

  // 移除未使用的统计逻辑

  // 获取选中任务的私信状态数据
  const fetchTaskMarketState = useCallback(async (taskId) => {
    if (!taskId) return;
    
    try {
      // 直接从tasks中查找任务，避免依赖filteredTasks
      const task = tasks.find(t => t.task_id === taskId);
      if (task) {
        const updatedTask = await getTaskMarketState(task);
        // 更新对应任务的私信状态数据
        setFilteredTasks(prevTasks => 
          prevTasks.map(t => 
            t.task_id === taskId ? updatedTask : t
          )
        );
        // 同时直接更新progressData
        if (updatedTask.progressData) {
          setProgressData({
            num: updatedTask.progressData.num || 0,
            sum: updatedTask.progressData.sum || 0,
            state: updatedTask.progressData.state || 0
          });
        }
      }
    } catch (error) {
      console.error('获取任务私信状态失败:', error);
    }
  }, [tasks, getTaskMarketState]);

  // 当选中任务变化时，获取该任务的私信状态数据
  useEffect(() => {
    if (selectedTaskId) {
      fetchTaskMarketState(selectedTaskId);
    }
  }, [selectedTaskId, fetchTaskMarketState]);

  // 测量容器宽度
  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    measureWidth();
    window.addEventListener('resize', measureWidth);
    
    return () => {
      window.removeEventListener('resize', measureWidth);
    };
  }, []);

  // 任务选择处理函数（优化版：懒加载状态）
  const handleTaskSelect = async (taskId) => {
    if (taskId !== selectedTaskId) {
      // 清除之前的防抖定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // 立即更新UI状态
      setSelectedTaskId(taskId);
      // 立即缓存到localStorage
      localStorage.setItem('privateCustomer_selectedTaskId', taskId);
      
      // 根据选中的任务设置默认消息内容
      const selectedTask = filteredTasks.find(task => task.task_id === taskId);
      if (selectedTask && selectedTask.keyword) {
        const defaultMessage = `有${selectedTask.keyword}需求吗？`;
        setInputValue(defaultMessage);
      }
      
      // 检查选中的任务是否已经获取过状态
      if (selectedTask && selectedTask.market_state === 'initial' && 
          selectedTask.progress === 0 && 
          selectedTask.progressData.sum === 0) {
        // 该任务还没有获取过真实状态，懒加载获取
        try {
          const originalTask = tasks.find(t => t.task_id === taskId);
          if (originalTask) {
            const updatedTask = await getTaskMarketState(originalTask);
            // 更新该任务的状态
            setFilteredTasks(prevTasks => 
              prevTasks.map(t => 
                t.task_id === taskId ? updatedTask : t
              )
            );
          }
        } catch (error) {
          console.error(`懒加载任务${taskId}状态失败:`, error);
        }
      }
    }
  };
  
  // 使用useMemo优化currentTask计算
  const currentTask = useMemo(() => {
    return filteredTasks.find(item => item.task_id === selectedTaskId);
  }, [filteredTasks, selectedTaskId]);
  
  const satisfiedTags = currentTask?.satisfiedTags || [];
  const unsatisfiedTags = currentTask?.unsatisfiedTags || [];
  const questionTags = currentTask?.questionTags || [];
  const inputPlaceholder = `有${currentTask ? currentTask.keyword : ''}需求吗？`;

  // 表格列配置
  const columns = [
    { 
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>用户昵称</span>
          <TableFilter
            onFilter={(value) => handleFilter('userNickname', value)}
            placeholder="请输入用户昵称"
          />
        </div>
      ), 
      dataIndex: 'user_name', 
      key: 'user_name',
      render: (text, record) => {
        return record.user_link ? (
          <a href={record.user_link} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : text;
      }
    },
    { 
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>IP地址</span>
          <RegionFilter
            onFilter={(value) => handleFilter('userRegion', value)}
            placeholder="请选择地区"
          />
        </div>
      ), 
      dataIndex: 'user_region', 
      key: 'user_region' 
    },
    { 
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>评论时间</span>
          <DateFilter
            onFilter={(value) => handleFilter('commentTime', value)}
            placeholder="请选择日期"
          />
        </div>
      ), 
      dataIndex: 'comment_time', 
      key: 'comment_time', 
      render: val => val || '-' 
    },
    { 
      title: (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          评论内容
        </div>
      ), 
      dataIndex: 'comment_content', 
      key: 'comment_content',
      render: (text, record) => {
        return record.content_link ? (
          <a href={record.content_link} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : text;
      }
    },
    { 
      title: (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          私信结果
        </div>
      ), 
      dataIndex: 'market_state', 
      key: 'market_state', 
      render: (val, record) => {
        const displayText = record.market_result_display || marketStateMap[val] || '未私信';
        return val === 'finish' ? (
          <Tag color="green">{displayText}</Tag>
        ) : val === 'initial' ? (
          <Tag color="gray">{displayText}</Tag>
        ) : val === 'processing' ? (
          <Tag color="orange">{displayText}</Tag>
        ) : (
          <Tag color="gray">{displayText}</Tag>
        );
      }
    }
  ];

  return (
    <Layout style={{ height: '100vh', background: '#fff' }}>
      <HomeHeader />
      <Layout style={{ height: 'calc(100vh - 64px)', flexDirection: 'row', marginLeft: 10 }}>
        <div style={{ width: 260 }}>
          <SidebarMenu
            tasks={filteredTasks}
            loading={taskLoading}
            selectedTaskId={selectedTaskId}
            onTaskSelect={handleTaskSelect}
            pageType="privateCustomer"
          />
        </div>
        <Content style={{ flex: 1, margin: 16, background: '#fff', borderRadius: 8, minWidth: 0, marginLeft: 10 }}>
          {/* 顶部标题区域 */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, marginTop: -9, marginLeft: -3 }}>
            <span style={{
              fontSize: 20,
              fontWeight: 600,
              marginRight: 18,
              marginLeft: 10,
              display: 'flex',
              alignItems: 'center'
            }}>
              {currentTask ? (
                <>
                  <PlatformIcon
                    platform={currentTask.platform}
                    style={{
                      fontSize: 20,
                      width: 24,
                      height: 24,
                      marginRight: 20,
                      marginLeft: 10,
                      verticalAlign: 'middle'
                    }}
                  />
                  {typeof currentTask.intent_count === 'number' && (
                    <span style={{ marginLeft: 10, color: 'black', fontSize: 20 }}>
                      {currentTask.keyword}
                      {currentTask.intent_count}个意向
                    </span>
                  )}
                </>
              ) : (
                '请选择任务'
              )}
            </span>
          </div>

          <InputWithTags
             inputValue={inputValue}
             onInputChange={setInputValue}
             satisfiedTags={satisfiedTags}
             unsatisfiedTags={unsatisfiedTags}
             questionTags={questionTags}
             placeholder={inputPlaceholder}
             taskId={selectedTaskId}
             containerWidth={containerWidth}
           />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 30,
            marginLeft: 10
          }}>
            {/* 左侧：导航标签 */}
            <div ref={containerRef} style={{ display: 'flex', alignItems: 'center' }}>
              <Tabs
                activeTab={activeTab}
                onChange={setActiveTab}
                style={{ minWidth: 200 }}
              >
                <Tabs.TabPane key="customers" title="意向客户" />
                <Tabs.TabPane key="private-message" title="前往私信" />
              </Tabs>
              {/* 右侧：进度条 */}
              <ProgressBar current={progressData.num} total={progressData.sum} />
            </div>
            {/* 数据统计 + 分页器 同行 */}
            {activeTab !== 'private-message' && (
              <PaginationToolbar>
                <HomePagination
                  total={detailTotal}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  setPageSize={(size) => {
                    // 如果页大小改变，使用专门的处理函数
                    handlePageSizeChange(size);
                  }}
                />
              </PaginationToolbar>
            )}
          </div>
          <div style={{ marginLeft: 10, marginRight: 10, marginTop: 20, marginBottom: 20 }}>
            {activeTab === 'customers' ? (
              <div style={{ position: 'relative', minHeight: '200px', width: '100%', background: '#eee' }}>
                <Table
                  columns={columns}
                  data={filteredDetailData}
                  loading={loading}
                  rowKey={record => record.comment_time + record.user_name}
                  pagination={false}
                  scroll={{ x: 1200 }}
                  size="small"
                  bordered={true}
                />
              </div>
            ) : (
               <div style={{ background: '#fff' }}>
                 <Table
                   columns={[
                     {
                       title: (
                         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '32px', width: '100%' }}>
                           <span>抖音账号</span>
                           <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: 'normal' }}>
                             <input type="radio" name="filterUsers" style={{ marginRight: '4px' }} />
                             只私信筛选用户
                           </label>
                         </div>
                       ),
                       dataIndex: 'account',
                       key: 'account'
                     },
                     {
                       title: '当前私信任务',
                       dataIndex: 'currentTask',
                       key: 'currentTask',
                       render: (text, record) => {
                         // 如果record中有task_id，直接从tasks数组中查找对应的任务标题
                         if (record.task_id) {
                           const task = tasks.find(t => t.task_id === record.task_id);
                           return task ? task.keyword : record.task_id;
                         }
                         return text || '无';
                       }
                     },
                     {
                       title: (
                         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                           <span>当前私信间隔 30 秒</span>
                           <button 
                             onClick={() => setSettingsModalVisible(true)}
                             style={{ 
                               padding: '4px 15px', 
                               background: '#1890ff', 
                               color: 'white', 
                               border: 'none', 
                               borderRadius: '6px', 
                               cursor: 'pointer', 
                               fontSize: '14px' 
                             }}
                           >
                             设置
                           </button>
                         </div>
                       ),
                       dataIndex: 'interval',
                       key: 'interval'
                     },
                     {
                       title: (
                         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                           <span>执行操作</span>
                           <button 
                             onClick={handleAddDouyinAccount}
                             disabled={accountsLoading}
                             style={{ 
                               padding: '4px 15px', 
                               background: accountsLoading ? '#ccc' : '#1890ff', 
                               color: 'white', 
                               border: 'none', 
                               borderRadius: '6px', 
                               cursor: accountsLoading ? 'not-allowed' : 'pointer', 
                               fontSize: '14px' 
                             }}
                           >
                             {accountsLoading ? '获取中...' : '新增抖音账号'}
                           </button>
                         </div>
                       ),
                       dataIndex: 'action',
                       key: 'action',
                       render: (text, record) => {
                          const isMessaging = messagingAccounts.has(record.user_id);
                          const state = record.state;
                          
                          return (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              {state === 1 ? (
                                <button 
                                  onClick={() => handleStartMessaging(record)}
                                  style={{
                                    padding: '4px 15px',
                                    background: '#3491fa',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  开始私信
                                </button>
                              ) : state === 2 ? (
                                <button 
                                  onClick={() => handleStopMessaging(record)}
                                  style={{
                                    padding: '4px 15px',
                                    background: '#f53f3f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  停止私信
                                </button>
                              ) : (state === 0 || state === 'not_logged_in') ? (
                                <button 
                                  onClick={() => handleLoginDouyinAccount(record)}
                                  style={{
                                    padding: '4px 15px',
                                    background: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  登录抖音账号
                                </button>
                              ) : (
                                <span style={{ color: '#999', fontSize: '14px' }}>-</span>
                              )}
                            </div>
                          );
                        }
                     }
                   ]}
                   data={douyinAccounts}
                   pagination={false}
                   loading={accountsLoading}
                   style={{ fontSize: '14px' }}
                 />
               </div>
            )}
          </div>
        </Content>
      </Layout>
      
      {/* 私信间隔设置弹窗 */}
      <Modal
        title={<span style={{ fontWeight: 'bold', textAlign: 'left' }}>私信间隔设置</span>}
        visible={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setSettingsModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={() => {
              // 这里可以添加保存设置的逻辑
              setSettingsModalVisible(false);
            }}
          >
            确认
          </Button>
        ]}
        style={{ width: 500 }}
        bodyStyle={{ borderTop: 'none', padding: 0 }}
        headerStyle={{ borderBottom: 'none' }}
      >
        <div style={{ padding: '10px 0' }}>
          <div style={{ marginBottom: 10, textAlign: 'left' }}>
            <span style={{ fontSize: 16, marginBottom: 10, display: 'block' }}>
              请设置私信间隔时间（0-300秒）：
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Slider
              value={privateMessageInterval}
              onChange={setPrivateMessageInterval}
              min={0}
              max={300}
              style={{ flex: 1 }}
            />
            <Button 
              onClick={() => setPrivateMessageInterval(Math.max(0, privateMessageInterval - 1))}
              style={{ width: 40, height: 40 }}
            >
              -
            </Button>
            <InputNumber
              value={privateMessageInterval}
              onChange={setPrivateMessageInterval}
              min={0}
              max={300}
              style={{ width: 50, textAlign: 'center', height: 40 }}
            />
            <Button 
              onClick={() => setPrivateMessageInterval(Math.min(300, privateMessageInterval + 1))}
              style={{ width: 40, height: 40 }}
            >
              +
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}